import {
	Component,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
	FormBuilder,
	FormGroup,
	Validators,
	ReactiveFormsModule,
} from '@angular/forms';
import { LoginInquiryModalComponent } from './login-inquiry-modal/login-inquiry-modal.component';
import { Store } from '@ngrx/store';
import {
	login,
	loginFailure,
	selectReturnUrl,
	setReturnUrl,
	State,
} from '@lmc-app/store';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { APP_VERSION } from '@lmc-app/version';
import { Actions, ofType } from '@ngrx/effects';
import { MatCard, MatCardContent } from '@angular/material/card';
import { NgOptimizedImage } from '@angular/common';
import { MatFormField, MatLabel, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./login.component.scss'],
	imports: [
		MatCard,
		MatCardContent,
		NgOptimizedImage,
		ReactiveFormsModule,
		MatFormField,
		MatLabel,
		MatInput,
		MatButton,
	],
})
export class LoginComponent implements OnInit, OnDestroy {
	loginFormGroup: FormGroup;
	protected readonly appVersion = APP_VERSION;
	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly formBuilder: FormBuilder,
		private readonly dialog: MatDialog,
		private readonly route: ActivatedRoute,
		private readonly store: Store<State>,
		private readonly actions$: Actions
	) {
		this.loginFormGroup = this.formBuilder.group({
			email: [null, Validators.required],
			password: [null, Validators.required],
		});
	}

	ngOnInit(): void {
		// Sync returnUrl from query params to NgRx store (backwards compatibility)
		// AuthGuard already sets this, but we sync here as fallback for edge cases
		const returnUrlFromQuery = this.route.snapshot?.queryParams['returnUrl'];
		if (returnUrlFromQuery) {
			// Check if store already has returnUrl, if not, set it from query params
			this.store
				.select(selectReturnUrl)
				.pipe(takeUntil(this.destroy$))
				.subscribe((storeReturnUrl) => {
					if (!storeReturnUrl && returnUrlFromQuery) {
						this.store.dispatch(
							setReturnUrl({ returnUrl: returnUrlFromQuery })
						);
					}
				});
		}

		// Listen for login failure to show error dialog
		this.actions$
			.pipe(ofType(loginFailure), takeUntil(this.destroy$))
			.subscribe(({ error }) => {
				this.handleLoginError(error);
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	login() {
		// Dispatch login action to NgRx store
		// This will:
		// 1. Call backend login API (set cookies, get user data)
		// 2. Update user state
		// 3. Navigate to returnUrl (from NgRx store) or /dashboard (handled by auth.effects.ts)
		this.store.dispatch(
			login({
				credentials: this.loginFormGroup.value,
			})
		);
	}

	openMoreDetails() {
		const username = this.loginFormGroup.controls.email.value;
		this.dialog.open(LoginInquiryModalComponent, {
			autoFocus: true,
			data: username,
		});
	}

	private handleLoginError(error: HttpErrorResponse) {
		let title: string;
		let content: string;

		switch (error.status) {
			case 0: {
				title = 'Connection Error';
				content =
					'Please check your internet connection or contact IT Support.';
				break;
			}
			case 401: {
				title = 'Invalid username or password';
				const remainingAttempts = error.error?.remainingAttempts;
				content = `${remainingAttempts} attempts remaining before account lock.`;
				break;
			}
			case 423: {
				title = 'Account Locked';
				const lockedUntil = error.error?.lockedUntil;
				const unlockTime = new Date(lockedUntil);
				const now = new Date();
				const minutesRemaining = Math.ceil(
					(unlockTime.getTime() - now.getTime()) / 60000
				);
				content = `Too many failed login attempts. Please try again in ${minutesRemaining} minutes.`;
				break;
			}
			case 500: {
				title = 'Internal Server Error';
				content = error.message;
				break;
			}
			default: {
				title = 'Unknown error';
				content = error.message;
				break;
			}
		}

		this.dialog.open(LmcDialogComponent, {
			data: {
				title,
				content,
				positiveButtonText: 'OK',
			},
		});
	}
}
